/**
 * Coerce a user/DB-provided value into a safe <img src>.
 *
 * We intentionally avoid turning bare filenames like `pieris.jpg` into
 * `https://pieris.jpg` (which causes DNS errors in the console).
 *
 * Allowed:
 * - http/https URLs
 * - protocol-relative URLs (`//example.com/...`) -> will be prefixed with current protocol
 * - same-origin absolute paths (`/uploads/...`)
 * - data/blob URLs
 */
export function coerceImageUrl(raw) {
  let url = (raw ?? '').toString().trim();
  if (!url || url.toLowerCase() === 'null') return null;

  // Strip surrounding quotes that may have been stored in DB
  url = url.replace(/^["']+|["']+$/g, '');

  // Fix malformed protocols: "https//" or "http//" (missing colon)
  url = url.replace(/^(https?)\/\//i, '$1://');

  // Guard: Cloudinary "upload/<transformation>" without a public id is incomplete.
  // Example: https://res.cloudinary.com/.../image/upload/c_crop:1
  // Browsers will request it and it 404s; treat as invalid.
  if (/cloudinary\.com/i.test(url) && /\/upload\/[^/]+$/i.test(url)) {
    const tail = url.split('/upload/')[1] || '';
    if (tail.includes(':') || tail.includes(',')) return null;
  }

  if (/^(data:|blob:)/i.test(url)) return url;
  if (url.startsWith('/')) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('//')) return `${window.location.protocol}${url}`;

  // If it looks like "example.com/path/to.jpg" (has a slash), we can safely assume
  // the user omitted protocol and add https://.
  if (url.includes('/')) return `https://${url}`;

  // Bare tokens like "pieris.jpg" or "example.com" are treated as invalid for images.
  return null;
}

export function isValidImageUrl(raw) {
  return Boolean(coerceImageUrl(raw));
}

/**
 * Absolute URL so the API server can `fetch()` the plan image.
 * Browsers may load `/uploads/...` via CRA proxy to :3001, but Node must use the API host.
 */
export function resolvePlanImageUrlForServerFetch(raw) {
  const url = (raw ?? '').toString().trim();
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('//')) {
    if (typeof window !== 'undefined' && window.location?.protocol) {
      return `${window.location.protocol}${url}`;
    }
    return `https:${url}`;
  }
  if (url.startsWith('/')) {
    const apiBase =
      (typeof process !== 'undefined' && process.env.REACT_APP_API_URL) ||
      (typeof process !== 'undefined' && process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');
    if (typeof apiBase === 'string' && /^https?:\/\//i.test(apiBase)) {
      const origin = apiBase.replace(/\/api\/?$/i, '') || apiBase;
      return `${origin.replace(/\/$/, '')}${url}`;
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${url}`;
    }
    return url;
  }
  return url;
}

